import React, { useState } from 'react';
import Chip from '@mui/material/Chip';
import {
  useManufacturerSourceDetail,
  useManufacturerLinkedProducts,
} from '../../../services/api/domains/manufacturers/hooks';
import { ManufacturerSourcePanel } from '../../../services/api/domains/manufacturers/types';
import { formatDateOnly } from '../../../utils/dateConversion';
import {
  GRAY_200,
  GRAY_400,
  GRAY_500,
  GRAY_700,
  GRAY_900,
} from 'theme/grayScale';
import { COLOR_DANGER } from 'theme/colors';
import { InfoTooltip } from '../../InfoTooltip';
import LgmButton from '../../LgmButton';
import LinkToSourceModal from './LinkToSourceModal';
import BreakLinkConfirmDialog from './BreakLinkConfirmDialog';
import LinkedProductsPanel from './LinkedProductsPanel';
import {
  SourcePanelPalette,
  DECRS_PANEL_COLORS,
  GLEIF_PANEL_COLORS,
  SAP_AML_PANEL_COLORS,
  SALESFORCE_PANEL_COLORS,
  CEP_PANEL_COLORS,
  DMF_PANEL_COLORS,
  ORANGE_BOOK_PANEL_COLORS,
  NDC_PANEL_COLORS,
} from 'theme/sourcePanelColors';

/** Sources with manual search/link support (MFGV2-003, Sub-task 3.2) — mirrors
 * which sources have a `searchColumn` set in api/sql/manufacturerLinkSources.js. */
const MANUALLY_LINKABLE_SOURCES = new Set([
  'decrs',
  'gleif',
  'sap_aml',
  'ndc',
  'orange_book',
  'usdmf',
]);

/** Sources with a real linked-products endpoint wired (MFGV2-004 Sub-task
 * 4.4, MFGV2-005 Sub-task 5.4, MFGV2-006 Sub-task 6.4) — NDC, Orange Book,
 * and USDMF all have product-catalog data behind their links. */
const SOURCES_WITH_LINKED_PRODUCTS = new Set(['ndc', 'orange_book', 'usdmf']);

/** Mirrors each source's `searchColumn` (api/sql/manufacturerLinkSources.js)
 * — the field that best identifies a linked record to a human in the unlink
 * confirmation dialog. */
const DISPLAY_FIELD_BY_SOURCE: Record<string, string> = {
  decrs: 'firm_name',
  gleif: 'legal_name',
  sap_aml: 'vendor_name',
  ndc: 'labeler_name',
  orange_book: 'applicant_name',
  usdmf: 'holder',
};

/** Field-label override for the fixed-order sources (same 3 as
 * SOURCES_WITH_LINKED_PRODUCTS below — issue #654 Seq 16 QA follow-up: card
 * uses (Manufacturer Name, Link Date, Match Basis) order instead of the
 * generic match-basis-first layout every other source keeps) — one shared
 * "Manufacturer Name" label instead of each source's own raw column name
 * (labeler_name/applicant_name/holder), since all three represent the same
 * concept: the manufacturer's name as recorded by that source. */
const NAME_LABEL_OVERRIDE = 'Manufacturer Name';
const LINKED_DATE_LABEL_OVERRIDE = 'Link Date';

/** One color per real-world source (issue #653, Task 16 QA follow-up) — USDMF/Orange Book/NDC reuse the product modal's own panels for the same source. */
const PANEL_COLORS_BY_KEY: Record<string, SourcePanelPalette> = {
  decrs: DECRS_PANEL_COLORS,
  gleif: GLEIF_PANEL_COLORS,
  sap_aml: SAP_AML_PANEL_COLORS,
  salesforce: SALESFORCE_PANEL_COLORS,
  cep: CEP_PANEL_COLORS,
  usdmf: DMF_PANEL_COLORS,
  orange_book: ORANGE_BOOK_PANEL_COLORS,
  ndc: NDC_PANEL_COLORS,
};
const DEFAULT_PANEL_COLORS: SourcePanelPalette = {
  border: GRAY_200,
  background: '#fff',
  headerBackground: GRAY_200,
  headerText: GRAY_700,
  badgeBackground: GRAY_200,
  badgeText: GRAY_700,
};

/** Badge text override for sources shared with the product modal's own
 * panels (issue #653 header redesign) — everything else keeps panel.label
 * as-is (DECRS, GLEIF, SAP AML, Salesforce, CEP). */
const PANEL_BADGE_LABEL_BY_KEY: Record<string, string> = {
  usdmf: 'FDA - USDMF',
  orange_book: 'FDA - Orange Book',
  ndc: 'FDA - NDC',
};

/** One-line description per source, shown as a header tooltip. */
const SOURCE_DESCRIPTIONS: Record<string, string> = {
  decrs:
    "FDA's Drug Establishment Current Registration Site system — facility registration and FEI data.",
  gleif:
    'The Global LEI Foundation — legal entity identifiers and corporate registration data.',
  sap_aml:
    "LGM's own SAP Approved Manufacturer List — vendor approval status and site data.",
  salesforce: "LGM's Salesforce CRM account records.",
  cep: 'EDQM Certificates of Suitability — facility-level, rolled up here from this manufacturer’s facilities.',
  usdmf:
    "FDA's Drug Master File system — confidential manufacturing-process filings.",
  orange_book:
    "FDA's Orange Book — approved drug products with therapeutic equivalence evaluations.",
  ndc: "FDA's National Drug Code directory.",
};

/** What each match_basis value from the linking pipeline actually means (api/sql/manufacturerLinking.js's MATCH_BASIS). */
const MATCH_BASIS_TOOLTIPS: Record<string, string> = {
  'DUNS-exact':
    'Matched on an exact DUNS number — the highest-confidence match, applied automatically.',
  SAFE: 'Name similarity of 95% or higher — high-confidence match, applied automatically.',
  REVIEW:
    'Name similarity of 85–94% — queued for human review, not applied automatically.',
  WEAK: 'Name similarity of 78–84% — low-confidence, queued for human review.',
  FP_RISK:
    'Below the safe-match threshold, high risk of a false positive — queued for human review.',
  human: 'Manually approved by a reviewer through the quarantine queue.',
};

/** 'fei_number' -> 'Fei Number' — good enough for the raw snapshot columns this renders. */
function humanizeKey(key: string): string {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

const PresentInLatestRunBadge: React.FC<{ value: boolean | null }> = ({
  value,
}) => {
  // No chip when unknown — the header's "No completed import yet" text
  // (rendered whenever datasetRefreshDate is null, which is what drives
  // value === null in practice) already covers this state.
  if (value === null) {
    return null;
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <Chip
        label={value ? 'Present in latest run' : 'Missing from latest run'}
        size="small"
        color={value ? 'success' : 'warning'}
        style={{ height: '18px', fontSize: '10px' }}
      />
      <InfoTooltip
        text={
          value
            ? 'This manufacturer was present in the most recently completed import from this source.'
            : "This manufacturer's record from this source was not seen in the most recently completed import — it may have been removed or renamed upstream."
        }
      />
    </span>
  );
};

/**
 * Owns its own page state — separate from the source-detail panel above it
 * — so paging through linked products never refetches or re-renders the
 * (unrelated) linked-records list. Scoped to ONE linked record's
 * `naturalKey` (issue #654 Seq 16 QA follow-up) — a manufacturer with more
 * than one linked record for this source (e.g. two distinct NDC labelers)
 * gets one of these per record, not one combined list for the whole panel.
 */
const LinkedProductsSection: React.FC<{
  manufacturerId: number;
  sourceKey: string;
  sourceLabel: string;
  naturalKey: string;
}> = ({ manufacturerId, sourceKey, sourceLabel, naturalKey }) => {
  const [page, setPage] = useState(0);
  const productsQuery = useManufacturerLinkedProducts(
    manufacturerId,
    sourceKey,
    page,
    10,
    naturalKey
  );

  // Stale-while-revalidate (MFGV3-002, Sub-task 2.1): `productsQuery.data`
  // already carries the PREVIOUS page's response while a same-list refetch
  // is in flight (`useManufacturerLinkedProducts`'s own fallback), so a
  // page-change refetch reports 'success' here too — the bare 'loading'
  // state is reserved for a genuine first load with nothing to show yet.
  const status =
    productsQuery.data !== undefined
      ? 'success'
      : productsQuery.status === 'error'
        ? 'error'
        : 'loading';

  return (
    <LinkedProductsPanel
      sourceLabel={sourceLabel}
      groupKey={naturalKey}
      totalCount={productsQuery.data?.totalCount ?? 0}
      products={productsQuery.data?.products ?? []}
      page={page}
      pageSize={10}
      status={status}
      isRefetching={productsQuery.isRefetching}
      onPageChange={setPage}
    />
  );
};

const FieldBox: React.FC<{
  label: string;
  value: string;
  tooltip?: string;
}> = ({ label, value, tooltip }) => (
  <div style={{ fontSize: '12px' }}>
    <div
      style={{
        color: GRAY_500,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
      }}>
      {label}
      {tooltip && <InfoTooltip text={tooltip} />}
    </div>
    <div style={{ color: GRAY_900 }}>{value}</div>
  </div>
);

const SourceRecordCard: React.FC<{
  record: Record<string, unknown>;
  sourceKey: string;
  sourceLabel: string;
  manufacturerId: number;
  onUnlink?: () => void;
}> = ({ record, sourceKey, sourceLabel, manufacturerId, onUnlink }) => {
  const matchBasis = record.match_basis as string | undefined;
  const matchBasisTooltip =
    MATCH_BASIS_TOOLTIPS[matchBasis ?? ''] ?? 'How this link was established.';
  const matchBasisField = matchBasis ? (
    <FieldBox
      key="match_basis"
      label="Match Basis"
      value={matchBasis}
      tooltip={matchBasisTooltip}
    />
  ) : null;

  const baseEntries = Object.entries(record).filter(
    ([key, value]) =>
      key !== 'match_basis' &&
      key !== 'natural_key' &&
      value !== null &&
      value !== undefined &&
      value !== ''
  );

  const showsLinkedProducts = SOURCES_WITH_LINKED_PRODUCTS.has(sourceKey);
  let fields: React.ReactNode[];
  if (showsLinkedProducts) {
    // Manufacturer Name, Link Date, Match Basis — issue #654 Seq 16 QA
    // follow-up; distinct from every other source's match-basis-first order.
    const displayField = DISPLAY_FIELD_BY_SOURCE[sourceKey];
    const nameEntry = baseEntries.find(([key]) => key === displayField);
    const dateEntry = baseEntries.find(([key]) => key === 'linked_date');
    const restEntries = baseEntries.filter(
      ([key]) => key !== displayField && key !== 'linked_date'
    );
    fields = [
      nameEntry && (
        <FieldBox
          key={nameEntry[0]}
          label={NAME_LABEL_OVERRIDE}
          value={String(nameEntry[1])}
        />
      ),
      dateEntry && (
        <FieldBox
          key={dateEntry[0]}
          label={LINKED_DATE_LABEL_OVERRIDE}
          value={String(dateEntry[1])}
        />
      ),
      matchBasisField,
      ...restEntries.map(([key, value]) => (
        <FieldBox key={key} label={humanizeKey(key)} value={String(value)} />
      )),
    ].filter(Boolean);
  } else {
    fields = [
      matchBasisField,
      ...baseEntries.map(([key, value]) => (
        <FieldBox key={key} label={humanizeKey(key)} value={String(value)} />
      )),
    ].filter(Boolean);
  }

  const naturalKey = String(record.natural_key ?? '');

  return (
    <div
      style={{
        padding: '8px',
        border: `1px solid ${GRAY_200}`,
        borderRadius: '4px',
        backgroundColor: '#fff',
        position: 'relative',
      }}>
      {onUnlink && (
        <button
          type="button"
          onClick={onUnlink}
          style={{
            position: 'absolute',
            top: '6px',
            right: '6px',
            fontSize: '0.6875rem',
            color: COLOR_DANGER,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px 4px',
          }}>
          Unlink
        </button>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: '6px',
          paddingRight: onUnlink ? '44px' : 0,
        }}>
        {fields}
      </div>
      {showsLinkedProducts && naturalKey && (
        <div
          style={{
            marginTop: '8px',
            paddingTop: '8px',
            borderTop: `1px solid ${GRAY_200}`,
          }}>
          <LinkedProductsSection
            key={naturalKey}
            manufacturerId={manufacturerId}
            sourceKey={sourceKey}
            sourceLabel={sourceLabel}
            naturalKey={naturalKey}
          />
        </div>
      )}
    </div>
  );
};

const SourcePanel: React.FC<{
  panel: ManufacturerSourcePanel;
  manufacturerId: number;
  onLink: () => void;
  onUnlink: (record: Record<string, unknown>) => void;
}> = ({ panel, manufacturerId, onLink, onUnlink }) => {
  const colors = PANEL_COLORS_BY_KEY[panel.key] ?? DEFAULT_PANEL_COLORS;
  const canManuallyLink = MANUALLY_LINKABLE_SOURCES.has(panel.key);
  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: '4px',
        padding: '10px',
        backgroundColor: colors.background,
      }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          margin: '-10px -10px 6px',
          padding: '6px 10px',
          borderRadius: '4px 4px 0 0',
          backgroundColor: colors.headerBackground,
        }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: colors.badgeText,
              backgroundColor: colors.badgeBackground,
              padding: '2px 6px',
              borderRadius: '4px',
            }}>
            {PANEL_BADGE_LABEL_BY_KEY[panel.key] ?? panel.label}
          </span>
          {SOURCE_DESCRIPTIONS[panel.key] && (
            <InfoTooltip text={SOURCE_DESCRIPTIONS[panel.key]} />
          )}
        </span>
        {panel.connected && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PresentInLatestRunBadge value={panel.presentInLatestRun} />
            <span style={{ fontSize: '11px', color: colors.headerText }}>
              {panel.datasetRefreshDate
                ? `Dataset refreshed ${formatDateOnly(panel.datasetRefreshDate)}`
                : 'No completed import yet'}
            </span>
            {canManuallyLink && (
              <LgmButton
                variant="flat"
                onClick={onLink}
                style={{ fontSize: '11px', padding: '2px 8px' }}>
                Link
              </LgmButton>
            )}
          </div>
        )}
      </div>
      {!panel.connected ? (
        <div style={{ fontSize: '13px', color: GRAY_400, fontStyle: 'italic' }}>
          Link coming soon
        </div>
      ) : (
        <>
          {panel.records.length === 0 ? (
            <div
              style={{
                fontSize: '13px',
                color: GRAY_400,
                fontStyle: 'italic',
              }}>
              Not linked to any {panel.label} record yet
            </div>
          ) : (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {panel.records.map((record, idx) => (
                <SourceRecordCard
                  key={idx}
                  record={record}
                  sourceKey={panel.key}
                  sourceLabel={panel.label}
                  manufacturerId={manufacturerId}
                  onUnlink={
                    canManuallyLink ? () => onUnlink(record) : undefined
                  }
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface DetailsTabProps {
  manufacturerId: number;
}

/**
 * Manufacturer modal Details tab — one panel per registered source (issue
 * #653, Task 10, Sub-task 10.2). Unconnected sources render "Link coming
 * soon" with no query issued for them (Open Question 4b).
 */
const DetailsTab: React.FC<DetailsTabProps> = ({ manufacturerId }) => {
  const panelsQuery = useManufacturerSourceDetail(manufacturerId);
  const [linkModalSource, setLinkModalSource] = useState<{
    key: string;
    label: string;
  } | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<{
    key: string;
    label: string;
    naturalKey: string;
    displayName: string;
  } | null>(null);

  if (panelsQuery.status === 'error') {
    return (
      <div style={{ color: COLOR_DANGER, fontSize: '13px', padding: '8px 0' }}>
        Failed to load source details. Try closing and reopening the modal.
      </div>
    );
  }

  if (panelsQuery.status === 'idle' || panelsQuery.status === 'loading') {
    return (
      <div style={{ color: GRAY_500, fontSize: '13px', padding: '8px 0' }}>
        Loading source details...
      </div>
    );
  }

  const panels = panelsQuery.data;

  if (panels.length === 0) {
    return (
      <div
        style={{
          fontSize: '13px',
          color: GRAY_400,
          fontStyle: 'italic',
          padding: '4px 0',
        }}>
        No sources registered
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {panels.map(panel => (
        <SourcePanel
          key={panel.key}
          panel={panel}
          manufacturerId={manufacturerId}
          onLink={() =>
            setLinkModalSource({ key: panel.key, label: panel.label })
          }
          onUnlink={record =>
            setUnlinkTarget({
              key: panel.key,
              label: panel.label,
              naturalKey: String(record.natural_key ?? ''),
              displayName: String(
                record[DISPLAY_FIELD_BY_SOURCE[panel.key]] ?? panel.label
              ),
            })
          }
        />
      ))}
      {linkModalSource && (
        <LinkToSourceModal
          manufacturerId={manufacturerId}
          source={linkModalSource.key}
          sourceLabel={linkModalSource.label}
          onClose={() => setLinkModalSource(null)}
          onLinked={() => {
            setLinkModalSource(null);
            // queryCache.invalidate() (inside useManualLinkManufacturer) does
            // not force an already-mounted useQuery to refetch — its
            // execute() never reads the cache in the first place (see the
            // "TODO: Re-enable caching" in services/api/hooks.ts). This
            // panel stays mounted underneath the modal the whole time it's
            // open, so an explicit refetch is the only thing that actually
            // shows the new link without the user closing/reopening.
            panelsQuery.refetch();
          }}
        />
      )}
      {unlinkTarget && (
        <BreakLinkConfirmDialog
          manufacturerId={manufacturerId}
          source={unlinkTarget.key}
          sourceLabel={unlinkTarget.label}
          naturalKey={unlinkTarget.naturalKey}
          displayName={unlinkTarget.displayName}
          onClose={() => setUnlinkTarget(null)}
          onUnlinked={() => {
            setUnlinkTarget(null);
            panelsQuery.refetch();
          }}
        />
      )}
    </div>
  );
};

export default DetailsTab;
